import { useTranslation } from "react-i18next";
import type { FormEvent } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { AuthMode } from "@/features/auth/hooks/auth/useLoginAuth";

interface LoginFormProps {
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  signupSource: string;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onSignupSourceChange: (value: string) => void;
}

const inputClassName =
  "mt-2 w-full rounded-sm border border-[var(--input)] bg-[var(--background)] px-4 py-3 text-sm text-[var(--foreground)] shadow-sm transition focus:border-[var(--ring)] focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]";

export default function LoginForm({
  mode,
  email,
  password,
  confirmPassword,
  name,
  signupSource,
  error,
  isSubmitting,
  onSubmit,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onNameChange,
  onSignupSourceChange,
}: LoginFormProps) {
  const { t } = useTranslation(["auth"]);
  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {mode === "register" && (
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-[var(--foreground)]">
            {t("auth:register.nameLabel")}
          </label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            className={inputClassName}
            placeholder={t("auth:register.namePlaceholder")}
          />
        </div>
      )}

      {mode === "register" && (
        <div>
          <label
            htmlFor="signupSource"
            className="block text-sm font-medium text-[var(--foreground)]"
          >
            {t("auth:register.signupSourceLabel")}
          </label>
          <select
            id="signupSource"
            value={signupSource}
            onChange={(event) => onSignupSourceChange(event.target.value)}
            className={inputClassName}
          >
            <option value="">{t("auth:register.signupSourcePlaceholder")}</option>
            <option value="linkedin">{t("auth:register.signupSourceOptions.linkedin")}</option>
            <option value="google">{t("auth:register.signupSourceOptions.google")}</option>
            <option value="referral">{t("auth:register.signupSourceOptions.referral")}</option>
            <option value="event">{t("auth:register.signupSourceOptions.event")}</option>
            <option value="other">{t("auth:register.signupSourceOptions.other")}</option>
          </select>
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-[var(--foreground)]">
          {t("auth:register.emailLabel")}
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          required
          className={inputClassName}
          placeholder={t("auth:login.emailPlaceholder")}
        />
      </div>

      {mode !== "reset" && (
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-[var(--foreground)]">
            {t("auth:register.passwordLabel")}
          </label>
          <input
            id="password"
            type="password"
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            required
            className={inputClassName}
            placeholder={t("auth:login.passwordPlaceholder")}
            minLength={6}
          />
        </div>
      )}

      {mode === "register" && (
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-[var(--foreground)]">
            {t("auth:register.confirmPasswordLabel")}
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => onConfirmPasswordChange(event.target.value)}
            required
            className={inputClassName}
            placeholder={t("auth:register.confirmPasswordPlaceholder")}
            minLength={6}
          />
        </div>
      )}

      {error && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        variant="cta"
        size="lg"
        disabled={isSubmitting}
        data-test="login-submit"
        className="w-full"
      >
        {isSubmitting
          ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner className="h-4 w-4" />
              {t("auth:login.processing")}
            </span>
          )
          : (
            <>
              {mode === "login" && t("auth:login.signInButton")}
              {mode === "register" && t("auth:register.createAccountButton")}
              {mode === "reset" && t("auth:reset.sendLinkButton")}
            </>
          )}
      </Button>
    </form>
  );
}
